import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import moment from 'moment';
import Modal from 'modules/views/shared/Modal';
import { ROUTES } from 'constants/routes';
import { ComputedRequirements, MissingItem, UpcomingGroup } from '../utils/requirementsCalculation';
import { useStyles } from './styles';

interface MissingRequirementsModalProps {
  isVisible: boolean;
  onClose: () => void;
  isLoading: boolean;
  requirementData: ComputedRequirements | null;
}

const MissingRequirementsModal: React.FC<MissingRequirementsModalProps> = ({
  isVisible,
  onClose,
  isLoading,
  requirementData,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState({
    missingCertifications: false,
    missingCourses: false,
    missingTrainings: false,
    upcomingCertifications: false,
    upcomingCourses: false,
    upcomingTrainings: false,
  });

  const toggle = (key: keyof typeof expanded) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  const renderItem = (item: MissingItem, index: number, sectionKey: string) => (
    <Box
      key={index}
      className={classes.requirementItem}
      data-testid={`missing-requirements-item-${sectionKey}-${index}`}
    >
      <Box className={classes.requirementItemContent}>
        <p className={classes.requirementItemName}>
          {item.name}
          {item.mandatoryLabel ? ` (${item.mandatoryLabel})` : ''}
        </p>
        {item.groupName && <p className={classes.requirementItemDescription}>{item.groupName}</p>}
      </Box>
      <span className={classes.missingBadge}>{t('projectList.missingRequirementsModal.badge.missing')}</span>
    </Box>
  );

  const renderSection = ({
    sectionKey,
    title,
    items,
    link,
    linkPath,
    note,
  }: {
    sectionKey: keyof typeof expanded;
    title: string;
    items: MissingItem[];
    link?: string;
    linkPath?: string;
    note?: string;
  }) => {
    if (!items || items.length === 0) return null;

    const isExpanded = expanded[sectionKey];
    const count = items.length;
    const countLabel =
      count === 1
        ? t('projectList.missingRequirementsModal.itemRequired', { count })
        : t('projectList.missingRequirementsModal.itemsRequired', { count });

    return (
      <Box className={classes.section} data-testid={`missing-requirements-section-${sectionKey}`}>
        <Box className={classes.sectionHeader}>
          <Box className={classes.sectionHeaderRow}>
            <Box>
              <p className={classes.sectionTitle}>{title}</p>
              <p className={classes.sectionCount} data-testid={`missing-requirements-count-${sectionKey}`}>
                {countLabel}
              </p>
            </Box>
            {link && linkPath && (
              <span
                className={classes.sectionLink}
                data-testid={`missing-requirements-link-${sectionKey}`}
                onClick={() => handleNavigate(linkPath)}
              >
                {link}
              </span>
            )}
          </Box>
          <span
            className={classes.toggleButton}
            data-testid={`missing-requirements-toggle-${sectionKey}`}
            onClick={() => toggle(sectionKey)}
          >
            {isExpanded ? (
              <>
                <ExpandLessIcon fontSize="small" />
                {t('projectList.missingRequirementsModal.hideDetails')}
              </>
            ) : (
              <>
                <ExpandMoreIcon fontSize="small" />
                {t('projectList.missingRequirementsModal.showDetails')}
              </>
            )}
          </span>
        </Box>
        <Collapse in={isExpanded}>
          <Box className={classes.collapseContent}>
            {note && (
              <Box className={classes.noteBox}>
                <span className={classes.noteLabel}>{t('projectList.missingRequirementsModal.noteLabel')}</span>
                <span className={classes.noteText}>{note}</span>
              </Box>
            )}
            {items.map((item, index) => renderItem(item, index, sectionKey))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const getUpcomingGroupNote = (group: UpcomingGroup): string => {
    const {
      effectiveDate,
      isExistingWorkerForGroup,
      existingWorkerGracePeriodInDays,
      newWorkerGracePeriodInDays,
      existingWorkerMandatoryDeadline,
      newWorkerMandatoryDeadline,
      workerBadgeFirstActivatedAt,
    } = group;

    const isInfiniteEffectiveDate = new Date(effectiveDate)?.getFullYear() >= 9999;
    const gracePeriodInDays =
      (isExistingWorkerForGroup ? existingWorkerGracePeriodInDays : newWorkerGracePeriodInDays) ?? 0;
    const mandatoryDeadline =
      (isExistingWorkerForGroup ? existingWorkerMandatoryDeadline : newWorkerMandatoryDeadline) ?? null;

    if (!workerBadgeFirstActivatedAt && mandatoryDeadline) {
      if (gracePeriodInDays > 0) {
        return t('projectList.missingRequirementsModal.note.gracePeriodPreactive', {
          days: gracePeriodInDays,
          date: moment(mandatoryDeadline).format('ll'),
        });
      }
      return t('projectList.missingRequirementsModal.note.effectiveFrom', {
        date: moment(mandatoryDeadline).format('ll'),
      });
    }

    return isInfiniteEffectiveDate
      ? t('projectList.missingRequirementsModal.note.gracePeriod', { days: gracePeriodInDays })
      : t('projectList.missingRequirementsModal.note.effectiveFrom', {
          date: moment(effectiveDate).format('ll'),
        });
  };

  const renderUpcomingSection = ({
    sectionKey,
    title,
    groups,
    link,
    linkPath,
  }: {
    sectionKey: keyof typeof expanded;
    title: string;
    groups: UpcomingGroup[];
    link?: string;
    linkPath?: string;
  }) => {
    const allItems = groups.flatMap(g => g.items);
    if (!allItems || allItems.length === 0) return null;

    const isExpanded = expanded[sectionKey];
    const count = allItems.length;
    const countLabel =
      count === 1
        ? t('projectList.missingRequirementsModal.itemRequired', { count })
        : t('projectList.missingRequirementsModal.itemsRequired', { count });

    return (
      <Box className={classes.section} data-testid={`missing-requirements-section-${sectionKey}`}>
        <Box className={classes.sectionHeader}>
          <Box className={classes.sectionHeaderRow}>
            <Box>
              <p className={classes.sectionTitle}>{title}</p>
              <p className={classes.sectionCount} data-testid={`missing-requirements-count-${sectionKey}`}>
                {countLabel}
              </p>
            </Box>
            {link && linkPath && (
              <span
                className={classes.sectionLink}
                data-testid={`missing-requirements-link-${sectionKey}`}
                onClick={() => handleNavigate(linkPath)}
              >
                {link}
              </span>
            )}
          </Box>
          <span
            className={classes.toggleButton}
            data-testid={`missing-requirements-toggle-${sectionKey}`}
            onClick={() => toggle(sectionKey)}
          >
            {isExpanded ? (
              <>
                <ExpandLessIcon fontSize="small" />
                {t('projectList.missingRequirementsModal.hideDetails')}
              </>
            ) : (
              <>
                <ExpandMoreIcon fontSize="small" />
                {t('projectList.missingRequirementsModal.showDetails')}
              </>
            )}
          </span>
        </Box>
        <Collapse in={isExpanded}>
          <Box className={classes.collapseContent}>
            {groups.map((group, groupIndex) => (
              <Box key={groupIndex} data-testid={`missing-requirements-group-${sectionKey}-${groupIndex}`}>
                <Box className={classes.noteBox}>
                  <span className={classes.noteLabel}>{t('projectList.missingRequirementsModal.noteLabel')}</span>
                  <span className={classes.noteText}>{getUpcomingGroupNote(group)}</span>
                </Box>
                {group.items.map((item, index) => renderItem(item, index, `${sectionKey}-${groupIndex}`))}
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Modal
      isVisible={isVisible}
      title={t('projectList.missingRequirementsModal.title')}
      modalStyleClass={classes.modal}
      modalContentStyleClass={classes.modalContent}
      testId="missing-requirements-modal"
      Content={
        isLoading ? (
          <Box className={classes.loadingContainer} data-testid="missing-requirements-loader">
            <CircularProgress />
          </Box>
        ) : (
          <Box className={classes.sectionsContainer}>
            {renderSection({
              sectionKey: 'missingCertifications',
              title: t('projectList.missingRequirementsModal.certifications.title'),
              items: requirementData?.missingCertifications ?? [],
              link: t('projectList.missingRequirementsModal.certifications.link'),
              linkPath: ROUTES.MY_CERTIFICATIONS.path,
            })}
            {renderSection({
              sectionKey: 'missingCourses',
              title: t('projectList.missingRequirementsModal.courses.title'),
              items: requirementData?.missingCourses ?? [],
              link: t('projectList.missingRequirementsModal.courses.link'),
              linkPath: ROUTES.MY_COURSES.path,
            })}
            {renderSection({
              sectionKey: 'missingTrainings',
              title: t('projectList.missingRequirementsModal.trainings.title'),
              items: requirementData?.missingTrainings ?? [],
            })}
            {renderUpcomingSection({
              sectionKey: 'upcomingCertifications',
              title: t('projectList.missingRequirementsModal.upcomingCertifications.title'),
              groups: requirementData?.upcomingCertifications ?? [],
              link: t('projectList.missingRequirementsModal.certifications.link'),
              linkPath: ROUTES.MY_CERTIFICATIONS.path,
            })}
            {renderUpcomingSection({
              sectionKey: 'upcomingCourses',
              title: t('projectList.missingRequirementsModal.upcomingCourses.title'),
              groups: requirementData?.upcomingCourses ?? [],
              link: t('projectList.missingRequirementsModal.courses.link'),
              linkPath: ROUTES.MY_COURSES.path,
            })}
            {renderUpcomingSection({
              sectionKey: 'upcomingTrainings',
              title: t('projectList.missingRequirementsModal.upcomingTrainings.title'),
              groups: requirementData?.upcomingTrainings ?? [],
            })}
          </Box>
        )
      }
      onClose={onClose}
    />
  );
};

export default MissingRequirementsModal;
